import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';
import { AssignmentHistory } from '../../types';

interface ActiveAssignmentsTableProps {
  assignments: AssignmentHistory[];
  onViewDetails: (assignment: AssignmentHistory) => void;
  onReceiveVehicle: (assignment: AssignmentHistory) => void;
}

export const ActiveAssignmentsTable: React.FC<ActiveAssignmentsTableProps> = ({
  assignments,
  onViewDetails,
  onReceiveVehicle,
}) => {
  const isWeb = Platform.OS === 'web';

  if (assignments.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Ionicons name="car-sport" size={24} color={theme.colors.primary} />
          <Text style={styles.title}>Active Assignments</Text>
        </View>
        <View style={styles.emptyState}>
          <Ionicons name="car-outline" size={48} color={theme.colors.gray300} />
          <Text style={styles.emptyText}>No active assignments</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="car-sport" size={24} color={theme.colors.primary} />
          <Text style={styles.title}>Active Assignments</Text>
        </View>
        <View style={styles.countBadge}>
          <Text style={styles.countText}>{assignments.length}</Text>
        </View>
      </View>

      <ScrollView horizontal={isWeb} showsHorizontalScrollIndicator={isWeb}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.headerCell, styles.plateColumn]}>License Plate</Text>
            <Text style={[styles.headerCell, styles.driverColumn]}>Driver</Text>
            <Text style={[styles.headerCell, styles.phoneColumn]}>Phone</Text>
            <Text style={[styles.headerCell, styles.dateColumn]}>Assigned Date</Text>
            <Text style={[styles.headerCell, styles.daysColumn]}>Days</Text>
            <Text style={[styles.headerCell, styles.actionsColumn]}>Actions</Text>
          </View>

          {/* Table Body */}
          <ScrollView style={styles.tableBody} showsVerticalScrollIndicator={true}>
            {assignments.map((assignment, index) => {
              const assignedDate = new Date(assignment.assignment_date);
              const today = new Date();
              const daysAssigned = Math.floor((today.getTime() - assignedDate.getTime()) / (1000 * 60 * 60 * 24));
              
              return (
                <View 
                  key={assignment.id} 
                  style={[
                    styles.tableRow,
                    index % 2 === 0 && styles.tableRowEven,
                  ]}
                >
                  <Text style={[styles.cell, styles.plateColumn, styles.plateText]}>
                    {assignment.vehicle?.license_plate || 'N/A'}
                  </Text>
                  <Text style={[styles.cell, styles.driverColumn]}>
                    {assignment.primaryDriver?.name || 'Unknown'}
                  </Text>
                  <Text style={[styles.cell, styles.phoneColumn, styles.phoneText]}>
                    {assignment.primaryDriver?.phone || 'N/A'}
                  </Text>
                  <Text style={[styles.cell, styles.dateColumn]}>
                    {assignedDate.toLocaleDateString()}
                  </Text>
                  <View style={[styles.cell, styles.daysColumn]}>
                    <View style={[
                      styles.daysBadge,
                      { backgroundColor: daysAssigned > 30 ? '#ffebee' : theme.colors.gray100 }
                    ]}>
                      <Text style={[
                        styles.daysText,
                        { color: daysAssigned > 30 ? theme.colors.red600 : theme.colors.textPrimary }
                      ]}>
                        {daysAssigned}
                      </Text>
                    </View>
                  </View>
                  <View style={[styles.cell, styles.actionsColumn, styles.actionsContainer]}>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.viewButton]}
                      onPress={() => onViewDetails(assignment)}
                    >
                      <Ionicons name="eye-outline" size={16} color={theme.colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.returnButton]}
                      onPress={() => onReceiveVehicle(assignment)}
                    >
                      <Ionicons name="checkmark-circle-outline" size={16} color={theme.colors.green600} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    ...theme.shadows.md,
    marginBottom: theme.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as '700',
    color: theme.colors.textPrimary,
    marginLeft: theme.spacing.sm,
  },
  countBadge: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.round,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  countText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '700' as '700',
    color: theme.colors.white,
  },
  table: {
    minWidth: Platform.OS === 'web' ? 800 : undefined,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray50,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.xs,
  },
  headerCell: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as '600',
    color: theme.colors.textSecondary,
    paddingHorizontal: theme.spacing.xs,
  },
  tableBody: {
    maxHeight: 400,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    alignItems: 'center',
  },
  tableRowEven: {
    backgroundColor: theme.colors.gray50,
  },
  cell: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    paddingHorizontal: theme.spacing.xs,
  },
  plateColumn: {
    width: 120,
  },
  driverColumn: {
    width: 150,
  },
  phoneColumn: {
    width: 120,
  },
  dateColumn: {
    width: 110,
  },
  daysColumn: {
    width: 70,
  },
  actionsColumn: {
    width: 100,
  },
  plateText: {
    fontWeight: '600' as '600',
    color: theme.colors.primary,
  },
  phoneText: {
    color: theme.colors.textSecondary,
  },
  daysBadge: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.sm,
    alignItems: 'center',
  },
  daysText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as '600',
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
  },
  actionButton: {
    padding: 6,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
  },
  viewButton: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.white,
  },
  returnButton: {
    borderColor: theme.colors.green600,
    backgroundColor: theme.colors.white,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.xl,
  },
  emptyText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    marginTop: theme.spacing.sm,
  },
});
